import _ from "lodash";
import cx from "classnames";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import styles from "./simulation.module.scss";

import Button from "src/components/button";
import {
  RTVIClientAudio,
  RTVIClientProvider,
  useRTVIClient,
  useRTVIClientEvent,
  useRTVIClientTransportState,
  VoiceVisualizer,
} from "realtime-ai-react";
import {
  LLMHelper,
  RTVIClient,
  RTVIError,
  RTVIEvent,
  RTVIMessage,
  Transport,
} from "realtime-ai";
import { DailyTransport } from "@daily-co/realtime-ai-daily";
import { useDispatch } from "react-redux";
import { stopSimulation } from "src/redux/actions/simulation/simulations";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import Thumbnail from "src/components/thumbnail";
import Actions from "src/components/actionbar";
import Slider from "src/components/slider";
import Panel from "src/pages/asset/components/animation/panel";
import { OrganizationInfo, Persona, SimulationInfo } from "./about";

const apiUrl = `${process.env.REACT_APP_SIMULATION_API_URL}`;

interface SimulationViewProps {
  simulation: any;
}

export const SimulationView: React.FC<SimulationViewProps> = ({
  simulation,
}) => {
  const dispatch = useDispatch();

  const [voiceClientState, setVoiceClientState] = useState<RTVIClient | null>(
    null,
  );

  const [count, setCount] = useState(3);

  useEffect(() => {
    if (voiceClientState) {
      return;
    }

    const voiceClient = new RTVIClient({
      transport: new DailyTransport() as unknown as Transport,
      params: {
        baseUrl: `${apiUrl}/simulations/1.0/simulations/${simulation.id}/`,
        headers: new Headers({
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        }),
      },
      enableMic: false,
    });

    const llmHelper = new LLMHelper({});
    voiceClient.registerHelper("llm", llmHelper);

    setVoiceClientState(voiceClient);

    return () => {
      //leave();
    };
  }, []);

  useEffect(() => {
    if (count > 0) {
      const timer = setTimeout(() => setCount(count - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [count]);

  return (
    <div className={styles.content}>
      {voiceClientState && (
        <RTVIClientProvider client={voiceClientState!}>
          {voiceClientState ? (
            <SimulationSession simulation={simulation} count={count} />
          ) : (
            <SimulationCountdownMessage count={count} />
          )}
          <RTVIClientAudio />
        </RTVIClientProvider>
      )}
    </div>
  );
};

const SimulationCountdownMessage = ({ count }) => {
  const [animate, setAnimate] = useState(false);

  useEffect(() => {
    setAnimate(true);
    const timer = setTimeout(() => setAnimate(false), 500); // Remove class after animation
    return () => clearTimeout(timer);
  }, [count]);

  return (
    <section className={styles.countdown}>
      <div className={styles.wrapper}>
        <p className={styles.text}>call starts in...</p>
        <span className={cx(styles.number, { [styles.slideIn]: animate })}>
          {count}
        </span>
      </div>
    </section>
  );
};

interface SimulationSession {
  simulation: any;
  count: number;
}

const SimulationSession = ({ simulation, count }) => {
  const dispatch = useDispatch();

  const transportState = useRTVIClientTransportState();
  const voiceClient = useRTVIClient()!;

  const [appState, setAppState] = useState<
    "idle" | "ready" | "connecting" | "connected"
  >("idle");

  const [initialized, setInitialized] = useState(false);
  const [ready, setReady] = useState(false);

  const [error, setError] = useState<string | null>(null);

  const { loading, scenario } = useSelector((state: any) => {
    const loading = _.get(
      state,
      `scenarios.scenario[${simulation!.scenario}].loading`,
    );
    const scenario = _.get(
      state,
      `scenarios.scenario[${simulation!.scenario}].item`,
    );
    return {
      loading,
      scenario,
    };
  }, _.isEqual);

  const user = useSelector((state: any) => {
    return _.get(state, "users.me");
  });

  const mountedRef = useRef(false);

  const [showCallInfo, setShowCallInfo] = useState(false);
  const [showTranscription, setShowTranscription] = useState(false);
  const [callEnd, setCallEnd] = useState(false);

  const canShowEndButton = showCallInfo || showTranscription;

  useRTVIClientEvent(
    RTVIEvent.Error,
    useCallback((message: RTVIMessage) => {
      const errorData = message.data as { error: string; fatal: boolean };
      console.log(errorData);
      if (!errorData.fatal) return;
      setError(errorData.error);
    }, []),
  );

  useEffect(() => {
    if (voiceClient && !mountedRef.current) {
      voiceClient.initDevices();
      mountedRef.current = true;
    }
  }, [voiceClient, appState]);

  useEffect(() => {
    if (voiceClient && initialized) {
      start();
    }
  }, [voiceClient, initialized]);

  useEffect(() => {
    // Update app state based on voice client transport state.
    // We only need a subset of states to determine the ui state,
    // so this effect helps avoid excess inline conditionals.
    switch (transportState) {
      case "initialized":
      case "disconnected":
        setAppState("ready");
        setInitialized(true);
        break;
      case "authenticating":
      case "connecting":
        setAppState("connecting");
        break;
      case "connected":
      case "ready":
        setAppState("connected");
        if (count <= 1) {
          setReady(true);
        }
        break;
      default:
        setAppState("idle");
    }
  }, [transportState]);

  async function start() {
    if (!voiceClient) return;

    // Join the session
    try {
      // Disable the mic until the bot has joined
      // to avoid interrupting the bot's welcome message
      voiceClient.enableMic(false);
      await voiceClient.connect();
    } catch (e) {
      setError((e as RTVIError).message || "Unknown error occured");
      voiceClient.disconnect();
    }
  }

  async function leave() {
    console.log("Leaving call");
    await voiceClient.disconnect();
    dispatch(stopSimulation(simulation!.scenario));
  }

  const handleEndCall = () => {
    setCallEnd(true);
  };

  const handleCallInfo = () => {
    setShowCallInfo(false);
  };

  // const handleTranscription = () => {
  //   setShowTranscription(false);
  // };

  return (
    <div
      className={cx(styles.simulation, {
        [styles.modalOpened]: showTranscription || showCallInfo,
      })}
    >
      <div className={styles.header}>
        <div className={styles.scenarioDetail}>
          <img src="/images/coaching/logo.svg" alt="Coaching" />
          <span className={styles.line} />
          <div className={styles.details}>
            <h4 className={styles.name}>
              Discovery Call With {_.get(scenario, "prospect.name")}
            </h4>
          </div>
        </div>
        {!canShowEndButton && (
          <i
            className={cx("paperflite-close", styles.close)}
            onClick={() => setCallEnd(true)}
          ></i>
        )}
      </div>
      <div className={styles.body}>
        {ready ? (
          <>
            <div className={styles.participants}>
              <Participant scenario={scenario} />
              <SalesParticipant participant={user} />
            </div>
          </>
        ) : (
          <SimulationCountdownMessage count={count} />
        )}
      </div>
      {ready && (
        <Actions.Row className={styles.actionRow}>
          <Actions.Item>
            <div className={styles.timer}>
              <Timer
                duration={_.get(scenario, "callSettings.duration", 2) * 60}
                onFinish={() => leave()}
              />
            </div>
          </Actions.Item>
          <Actions.IconButton
            className={styles.infoButton}
            icon="paperflite-document"
            tooltip="Call Information"
            onClick={() => setShowCallInfo(true)}
          />
          <Actions.Button
            className={styles.endButton}
            title="end"
            icon="fa fa-phone-slash"
            width={2}
            onClick={() => handleEndCall()}
          ></Actions.Button>
        </Actions.Row>
      )}

      <Panel show={showCallInfo} className={styles.modal}>
        <CallInfoView scenario={scenario} onClose={handleCallInfo} />
      </Panel>

      {/* <Panel show={showTranscription} className={styles.modal}>
        <TranscriptionView onClose={handleTranscription} />
      </Panel> */}

      <EndCallSlider
        show={callEnd}
        onConfirm={leave}
        onClose={() => setCallEnd(false)}
      />
    </div>
  );
};

interface EndCallSliderProps {
  show: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

const EndCallSlider: React.FC<EndCallSliderProps> = ({
  show,
  onConfirm,
  onClose,
}) => {
  return (
    <Slider.Dialog
      className={styles.endDialog}
      show={show}
      width={"23.75rem"}
      onClose={onClose}
    >
      <Slider.Header
        title="ending call"
        message="Follow up call - VP of Marketing - Wayne Enterprises"
      />
      <Slider.Body>
        <span className={styles.endCallMessage}>
          Are you sure you would like to end this call?
        </span>
      </Slider.Body>
      <Slider.Actions>
        <Button
          className={styles.confirmButton}
          variant="gradient"
          icon="paperflite-tick"
          text="Confirm"
          onClick={onConfirm}
        />
        <Button variant="secondary" text="Cancel" onClick={onClose} />
      </Slider.Actions>
    </Slider.Dialog>
  );
};

interface CallInfoViewProps {
  scenario: any;
  onClose: () => void;
}

const CallInfoView: React.FC<CallInfoViewProps> = ({ scenario, onClose }) => {
  return (
    <section className={styles.information}>
      <div className={styles.verticalLine}></div>
      <header className={styles.header}>
        <h4>Call Info</h4>
        <i className="paperflite-close" onClick={() => onClose()}></i>
      </header>
      <div className={styles.content}>
        <SimulationInfo scenario={scenario} />

        <OrganizationInfo company={_.get(scenario, "company")} />

        <span className={styles.line} />

        <Persona prospect={_.get(scenario, "prospect")} />

        <span className={styles.line} />
      </div>
    </section>
  );
};

interface TranscriptionViewProps {
  onClose: () => void;
}

const TranscriptionView: React.FC<TranscriptionViewProps> = ({ onClose }) => {
  return (
    <section className={styles.transcription}>
      <div className={styles.verticalLine}></div>
      <header className={styles.header}>
        <h4>Transcription</h4>
        <i className="paperflite-close" onClick={() => onClose()}></i>
      </header>
      <div className={styles.content}>
        {_.times(5, (index) => (
          <Transcript key={index} />
        ))}
      </div>
    </section>
  );
};

const Transcript = () => {
  return (
    <div className={styles.transcript}>
      <div className={styles.timeStamp}>0:02</div>
      <div className={styles.profile}>
        <Thumbnail
          className={styles.icon}
          media={null}
          defaultImage={"/images/assets/default_asset.jpg"}
        />
        <span className={styles.name}>David</span>
      </div>
      <div className={styles.message}>
        <span className={styles.text}>
          Hi David, I am Manuj from Paperflite. I am excited to talk to you
          about our new product.
        </span>
      </div>
    </div>
  );
};

const SalesParticipant = ({ participant }) => {
  const volRef = useRef<HTMLDivElement>(null);

  const profileImage = useMemo(() => {
    const image = _.get(participant, "icon");
    if (image) {
      return {
        id: image.id,
        key: image.key,
        type: "image",
        full: image.full,
      } as any;
    }

    return null;
  }, [participant]);

  useRTVIClientEvent(
    RTVIEvent.LocalAudioLevel,
    useCallback((volume: number) => {
      if (volRef.current) {
        const scaled = volume * 3;
        const shadow = `0 0 ${8 * scaled}px ${4 * scaled}px #fff, 0 0 ${12 * scaled}px ${8 * scaled}px #f0f, 0 0 ${16 * scaled}px ${11 * scaled}px #0ff`;
        // volRef.current.style.boxShadow = shadow;
      }
    }, []),
  );

  return (
    <div className={styles.participant}>
      <div className={cx(styles.avatar)}>
        <Thumbnail media={profileImage} defaultImage="/images/albumcover.jpg" />
      </div>
      <div className={styles.name}>{participant.firstName}</div>
      
      <div ref={volRef} className={styles.voiceVisualizer}>
      <VoiceVisualizer
          participantType="local"
          barColor="#FF45CF"
          barGap={3}
          barWidth={8}
          barMaxHeight={24}
        />
      </div>
    </div>
  );
};

const Participant = ({ scenario }) => {
  const volRef = useRef<HTMLDivElement>(null);
  const voiceClient = useRTVIClient()!;
  const [started, setStarted] = useState(false);

  const profileImage = useMemo(() => {
    const image = _.get(scenario, "prospect.profileImage");
    if (image) {
      return {
        id: image,
        key: image,
        type: "image",
      } as any;
    }

    return null;
  }, [scenario]);

  useRTVIClientEvent(
    RTVIEvent.RemoteAudioLevel,
    useCallback((volume: number) => {
      if (volRef.current) {
        const scaled = volume * 10;
        const shadow = `0 0 ${8 * scaled}px ${4 * scaled}px #fff, 0 0 ${12 * scaled}px ${8 * scaled}px #f0f, 0 0 ${16 * scaled}px ${11 * scaled}px #0ff`;
        // volRef.current.style.boxShadow = shadow;
      }
    }, []),
  );

  useRTVIClientEvent(
    RTVIEvent.BotStoppedSpeaking,
    useCallback(() => {
      if (started) return;
      setStarted(true);
    }, [started]),
  );

  useEffect(() => {
    if (voiceClient && started) {
      voiceClient.enableMic(true);
    }
  }, [voiceClient, started]);

  return (
    <div className={styles.participant}>
      <div className={cx(styles.avatar)}>
        <Thumbnail media={profileImage} defaultImage="/images/albumcover.jpg" />
      </div>
      <div className={styles.name}>
        {_.get(scenario, "prospect.name", "David")}
      </div>

      <div ref={volRef} className={styles.voiceVisualizer}>
        <VoiceVisualizer
          participantType="bot"
          barColor="#FF45CF"
          barGap={2}
          barWidth={8}
          barMaxHeight={24}
        />
      </div>
    </div>
  );
};

interface TimerProps {
  duration: number;
  onFinish?: () => void;
}

const Timer: React.FC<TimerProps> = ({ duration, onFinish }) => {
  const [countDown, setCountDown] = useState(duration);

  useEffect(() => {
    const interval = setInterval(() => {
      setCountDown((time) => Math.max(time - 1, 0));
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    setCountDown(duration);
  }, [duration]);

  useEffect(() => {
    if (countDown <= 0) {
      if (onFinish) {
        onFinish();
      }
    }
  }, [countDown]);

  const display = useMemo(() => {
    const seconds = Math.floor(countDown % 60);
    const minutes = Math.floor((countDown / 60) % 60);
    return `${_.padStart(String(minutes), 2, "0")}:${_.padStart(String(seconds), 2, "0")}`;
  }, [countDown]);

  return <div className={styles.timer}>{display}</div>;
};